import { create } from 'zustand';
import { User, AuthResponse } from '@shared/types';
import { api } from '@/lib/api-client';
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  likedPromptIds: string[];
  purchasedPromptIds: string[];
  login: (credentials: { email: string; password: string }) => Promise<void>;
  register: (data: { email: string; password: string; name: string }) => Promise<void>;
  logout: () => void;
  initialize: () => Promise<void>;
  toggleLike: (promptId: string) => void;
  purchasePrompt: (promptId: string) => void;
}
export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('nexus_token'),
  isAuthenticated: !!localStorage.getItem('nexus_token'),
  isInitialized: false,
  likedPromptIds: [],
  purchasedPromptIds: [],
  initialize: async () => {
    const token = localStorage.getItem('nexus_token');
    if (!token) {
      set({ isInitialized: true, isAuthenticated: false, user: null });
      return;
    }
    try {
      const user = await api<User>('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      set({
        user,
        isAuthenticated: true,
        isInitialized: true,
        likedPromptIds: user.likedPromptIds || [],
        purchasedPromptIds: user.purchasedPromptIds || []
      });
    } catch (e) {
      console.error("Auth init failed", e);
      localStorage.removeItem('nexus_token');
      set({ user: null, token: null, isAuthenticated: false, isInitialized: true });
    }
  },
  login: async (credentials) => {
    const response = await api<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
    localStorage.setItem('nexus_token', response.token);
    set({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      likedPromptIds: response.user.likedPromptIds || [],
      purchasedPromptIds: response.user.purchasedPromptIds || []
    });
  },
  register: async (data) => {
    const response = await api<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    localStorage.setItem('nexus_token', response.token);
    set({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      likedPromptIds: response.user.likedPromptIds || [],
      purchasedPromptIds: response.user.purchasedPromptIds || []
    });
  },
  logout: () => {
    localStorage.removeItem('nexus_token');
    set({ 
      user: null, 
      token: null, 
      isAuthenticated: false, 
      likedPromptIds: [], 
      purchasedPromptIds: [] 
    });
  },
  toggleLike: (promptId: string) => {
    const current = get().likedPromptIds;
    const exists = current.includes(promptId);
    const next = exists
      ? current.filter(id => id !== promptId)
      : [...current, promptId];
    set({ likedPromptIds: next });
    const currentUser = get().user;
    if (currentUser) {
      set({ user: { ...currentUser, likedPromptIds: next } });
    }
  },
  purchasePrompt: (promptId: string) => {
    const current = get().purchasedPromptIds;
    if (current.includes(promptId)) return;
    const next = [...current, promptId];
    set({ purchasedPromptIds: next });
    const currentUser = get().user;
    if (currentUser) {
      set({ user: { ...currentUser, purchasedPromptIds: next } });
    }
  }
}));